import React, { useEffect, useState, useRef } from "react";
import { FiEdit } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { base_url } from "../utils/axiosConfig";

const EditDocumentModal = ({ show, onHide, documentType, updateDocument }) => {
  const fileRef = useRef(null);

  const documentSchema = Yup.object({
    file: Yup.mixed()
      .test("fileSize", "File size must be less than 5MB", (value) =>
        value ? value.size <= 5 * 1024 * 1024 : true
      )
      .test("fileType", "Only image files are allowed", (value) =>
        value
          ? ["image/jpeg", "image/png", "image/gif"].includes(value.type)
          : true
      ),
  });

  return (
    <Modal show={show} onHide={onHide} centered className="font-sans">
      <Modal.Body className="p-6">
        <div className="relative">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
          >
            <FaTimes size={16} />
          </button>
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Update {documentType.replace(/([A-Z])/g, " $1").trim()}
            </h2>
          </div>
          <Formik
            initialValues={{ file: null }}
            validationSchema={documentSchema}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              updateDocument(values.file, documentType);
              resetForm();
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Upload New Document
                  </label>
                  <div className="flex items-center gap-4 mt-1">
                    <button
                      type="button"
                      onClick={() => fileRef.current.click()}
                      className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                    >
                      Choose File
                    </button>
                    <span className="text-sm text-gray-600">
                      {values.file ? values.file.name : "No file chosen"}
                    </span>
                    <input
                      type="file"
                      name="file"
                      ref={fileRef}
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => setFieldValue("file", event.target.files[0])}
                    />
                  </div>
                  <ErrorMessage
                    name="file"
                    component="div"
                    className="text-red-600 text-xs mt-1"
                  />
                </div>
                <div className="flex justify-center gap-4 mt-6">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
                    onClick={onHide}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const Profile = () => {
  const getTokenFromLocalStorage = localStorage.getItem("user_website")
    ? JSON.parse(localStorage.getItem("user_website"))
    : null;

  const config2 = {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
      Accept: "application/json",
    },
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [currentDocumentType, setCurrentDocumentType] = useState("");

  // File input refs for KYC documents
  const panCardRef = useRef(null);
  const aadhaarCardRef = useRef(null);
  const passportOrVoterIdRef = useRef(null);
  const photographRef = useRef(null);
  const cancelledChequeRef = useRef(null);

  const editProfileSchema = Yup.object({
    firstname: Yup.string().required("First Name is required"),
    lastname: Yup.string().required("Last Name is required"),
    dob: Yup.date().required("DOB is required"),
    gender: Yup.string().required("Gender is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile: Yup.string()
      .matches(/^[0-9]+$/, "Not a Valid Phone Number")
      .required("Phone is required"),
    address: Yup.object({
      address_line: Yup.string().required("Street address is required"),
      city: Yup.string().required("City is required"),
      state: Yup.string().required("State is required"),
      country: Yup.string().required("Country is required"),
      pincode: Yup.string()
        .matches(/^[0-9]+$/, "Not a Valid Pincode")
        .required("Pincode is required"),
    }),
    panCardNumber: Yup.string(),
    aadhaarNumber: Yup.string(),
    bankDetails: Yup.object({
      accountHolderName: Yup.string(),
      bankName: Yup.string(),
      accountNumber: Yup.string(),
      ifscCode: Yup.string(),
      accountType: Yup.string(),
      upiId: Yup.string(),
    }),
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!getTokenFromLocalStorage?.token) {
        setError("Please log in to view your profile");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(`${base_url}v1/user/getUserProfile`, {
          method: "GET",
          headers: config2.headers,
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const { data } = await response.json();
        console.log("User data:", data);
        setUser(data);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setError(error.message);
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleEditClose = () => {
    setShowEditModal(false);
  };

  const handleDocumentModalOpen = (docType) => {
    setCurrentDocumentType(docType);
    setShowDocumentModal(true);
  };

  const handleDocumentModalClose = () => {
    setShowDocumentModal(false);
    setCurrentDocumentType("");
  };

  const handleEditProfile = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = new FormData();
      formData.append("firstname", values.firstname);
      formData.append("lastname", values.lastname);
      formData.append("dob", values.dob);
      formData.append("gender", values.gender);
      formData.append("email", values.email);
      formData.append("mobile", values.mobile);
      formData.append("address_line", values.address.address_line);
      formData.append("city", values.address.city);
      formData.append("state", values.address.state);
      formData.append("country", values.address.country);
      formData.append("pincode", values.address.pincode);
      formData.append("panCardNumber", values.panCardNumber);
      formData.append("aadhaarNumber", values.aadhaarNumber);
      formData.append("accountHolderName", values.bankDetails.accountHolderName);
      formData.append("bankName", values.bankDetails.bankName);
      formData.append("accountNumber", values.bankDetails.accountNumber);
      formData.append("ifscCode", values.bankDetails.ifscCode);
      formData.append("accountType", values.bankDetails.accountType);
      formData.append("upiId", values.bankDetails.upiId);
      if (values.panCard) formData.append("panCard", values.panCard);
      if (values.aadhaarCard) formData.append("aadhaarCard", values.aadhaarCard);
      if (values.passportOrVoterId) formData.append("passportOrVoterId", values.passportOrVoterId);
      if (values.photograph) formData.append("photograph", values.photograph);
      if (values.cancelledCheque) formData.append("cancelledCheque", values.cancelledCheque);

      console.log("FormData payload:", Object.fromEntries(formData));
      const response = await fetch(`${base_url}v1/user/updateUserProfile`, {
        method: "POST",
        headers: { Authorization: config2.headers.Authorization },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const { data } = await response.json();
      setUser(data);
      toast.success("Profile updated successfully!");
      resetForm();
      handleEditClose();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDocument = async (file, docType) => {
    try {
      const formData = new FormData();
      formData.append(docType, file);
      console.log("Document FormData payload:", Object.fromEntries(formData));
      const response = await fetch(`${base_url}v1/user/updateUserProfile`, {
        method: "POST",
        headers: { Authorization: config2.headers.Authorization },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const { data } = await response.json();
      setUser(data);
      toast.success(`${docType.replace(/([A-Z])/g, " $1").trim()} updated successfully!`);
      handleDocumentModalClose();
    } catch (error) {
      console.error(`Error updating ${docType}:`, error);
      toast.error(`Failed to update ${docType.replace(/([A-Z])/g, " $1").trim()}`);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600 font-sans">
          Loading your account...
        </p>
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg text-red-600 font-sans">{error}</p>
      </div>
    );
  if (!user)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600 font-sans">Account not found</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <style>
        {`
          .custom-modal .modal-dialog {
            max-width: 900px;
            width: 90%;
            margin: 1.75rem auto;
          }
        `}
      </style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-600 mb-6">
          <a href="/" className="hover:text-[#35A93E] transition-colors">
            Home
          </a>{" "}
          <span>|</span>{" "}
          <span>Profile</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full lg:w-1/4">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Your Account
              </h2>
              <ul className="space-y-2">
                <li>
                  <button
                    className="w-full text-left text-[#35A93E] hover:text-[#2d8a33] font-medium"
                    onClick={() => setShowEditModal(true)}
                  >
                    Profile Information
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full lg:w-3/4">
            {/* Profile Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                  Personal Information
                </h2>
                <button
                  className="flex items-center bg-[#35A93E] text-white px-4 py-2 rounded hover:bg-[#2d8a33] transition"
                  onClick={() => setShowEditModal(true)}
                >
                  <FiEdit className="mr-2" /> Edit
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Full Name
                  </label>
                  <p className="mt-1 text-gray-900">{`${user.firstname || ""} ${
                    user.lastname || ""
                  }`}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Email Address
                  </label>
                  <p className="mt-1 text-gray-900">{user.email || ""}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Mobile Number
                  </label>
                  <p className="mt-1 text-gray-900">{user.mobile || ""}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Date of Birth
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.dob ? new Date(user.dob).toLocaleDateString() : ""}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Gender
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.gender
                      ? user.gender.charAt(0).toUpperCase() +
                        user.gender.slice(1)
                      : ""}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Referral Code
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.referralCode || ""}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    PAN Card Number
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.panCardNumber || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Aadhaar Number
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.aadhaarNumber || "N/A"}
                  </p>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-4">
                Address
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Street Address
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.address?.address_line || ""}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    City
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.address?.city || ""}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    State
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.address?.state || ""}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Country
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.address?.country || ""}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Pincode
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.address?.pincode || ""}
                  </p>
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Bank Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Account Holder Name
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.bankDetails?.accountHolderName || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Bank Name
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.bankDetails?.bankName || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Account Number
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.bankDetails?.accountNumber || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    IFSC Code
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.bankDetails?.ifscCode || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Account Type
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.bankDetails?.accountType || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    UPI ID
                  </label>
                  <p className="mt-1 text-gray-900">
                    {user.bankDetails?.upiId || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* KYC Documents */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                KYC Documents
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      PAN Card
                    </label>
                    <div className="mt-1">
                      {user.kycDocuments?.panCard ? (
                        <img
                          src={user.kycDocuments.panCard}
                          alt="PAN Card"
                          className="w-32 h-32 object-contain border border-gray-300 rounded"
                          onError={(e) => (e.target.src = "/placeholder.png")}
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-600 text-sm border border-gray-300 rounded">
                          No Image
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDocumentModalOpen("panCard")}
                    className="mt-6 p-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                  >
                    <FiEdit />
                  </button>
                </div>
                <div className="flex items-start gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Aadhaar Card
                    </label>
                    <div className="mt-1">
                      {user.kycDocuments?.aadhaarCard ? (
                        <img
                          src={user.kycDocuments.aadhaarCard}
                          alt="Aadhaar Card"
                          className="w-32 h-32 object-contain border border-gray-300 rounded"
                          onError={(e) => (e.target.src = "/placeholder.png")}
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-600 text-sm border border-gray-300 rounded">
                          No Image
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDocumentModalOpen("aadhaarCard")}
                    className="mt-6 p-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                  >
                    <FiEdit />
                  </button>
                </div>
                <div className="flex items-start gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Passport or Voter ID
                    </label>
                    <div className="mt-1">
                      {user.kycDocuments?.passportOrVoterId ? (
                        <img
                          src={user.kycDocuments.passportOrVoterId}
                          alt="Passport or Voter ID"
                          className="w-32 h-32 object-contain border border-gray-300 rounded"
                          onError={(e) => (e.target.src = "/placeholder.png")}
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-600 text-sm border border-gray-300 rounded">
                          No Image
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDocumentModalOpen("passportOrVoterId")}
                    className="mt-6 p-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                  >
                    <FiEdit />
                  </button>
                </div>
                <div className="flex items-start gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Photograph
                    </label>
                    <div className="mt-1">
                      {user.kycDocuments?.photograph ? (
                        <img
                          src={user.kycDocuments.photograph}
                          alt="Photograph"
                          className="w-32 h-32 object-contain border border-gray-300 rounded"
                          onError={(e) => (e.target.src = "/placeholder.png")}
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-600 text-sm border border-gray-300 rounded">
                          No Image
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDocumentModalOpen("photograph")}
                    className="mt-6 p-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                  >
                    <FiEdit />
                  </button>
                </div>
                <div className="flex items-start gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Cancelled Cheque
                    </label>
                    <div className="mt-1">
                      {user.kycDocuments?.cancelledCheque ? (
                        <img
                          src={user.kycDocuments.cancelledCheque}
                          alt="Cancelled Cheque"
                          className="w-32 h-32 object-contain border border-gray-300 rounded"
                          onError={(e) => (e.target.src = "/placeholder.png")}
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-600 text-sm border border-gray-300 rounded">
                          No Image
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDocumentModalOpen("cancelledCheque")}
                    className="mt-6 p-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                  >
                    <FiEdit />
                  </button>
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-600">
                    KYC Status
                  </label>
                  <p className="mt-1 text-gray-900">
                    <span
                      className={`inline-block px-2 py-1 rounded text-sm ${
                        user.kycVerified
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {user.kycVerified ? "Verified" : "Not Verified"}
                    </span>
                  </p>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile Modal */}
        <Modal
          show={showEditModal}
          onHide={handleEditClose}
          centered
          className="font-sans custom-modal"
        >
          <Modal.Body className="p-8">
            <div className="relative">
              <button
                onClick={handleEditClose}
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
              >
                <FaTimes size={16} />
              </button>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                  Edit Your Information
                </h2>
                <p className="text-sm text-gray-600">
                  Update your personal, address, bank details, and KYC documents
                </p>
              </div>
              <Formik
                initialValues={{
                  firstname: user.firstname || "",
                  lastname: user.lastname || "",
                  dob: user.dob ? user.dob.split("T")[0] : "",
                  gender: user.gender || "",
                  email: user.email || "",
                  mobile: user.mobile || "",
                  address: {
                    address_line: user.address?.address_line || "",
                    city: user.address?.city || "",
                    state: user.address?.state || "",
                    country: user.address?.country || "",
                    pincode: user.address?.pincode || "",
                  },
                  panCardNumber: user.panCardNumber || "",
                  aadhaarNumber: user.aadhaarNumber || "",
                  bankDetails: {
                    accountHolderName: user.bankDetails?.accountHolderName || "",
                    bankName: user.bankDetails?.bankName || "",
                    accountNumber: user.bankDetails?.accountNumber || "",
                    ifscCode: user.bankDetails?.ifscCode || "",
                    accountType: user.bankDetails?.accountType || "",
                    upiId: user.bankDetails?.upiId || "",
                  },
                  panCard: null,
                  aadhaarCard: null,
                  passportOrVoterId: null,
                  photograph: null,
                  cancelledCheque: null,
                }}
                validationSchema={editProfileSchema}
                onSubmit={handleEditProfile}
                enableReinitialize
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          First Name
                        </label>
                        <Field
                          type="text"
                          name="firstname"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="firstname"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Last Name
                        </label>
                        <Field
                          type="text"
                          name="lastname"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="lastname"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Email Address
                        </label>
                        <Field
                          type="text"
                          name="email"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Mobile Number
                        </label>
                        <Field
                          type="text"
                          name="mobile"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="mobile"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Date of Birth
                        </label>
                        <Field
                          type="date"
                          name="dob"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                          max={new Date().toISOString().split("T")[0]}
                        />
                        <ErrorMessage
                          name="dob"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Gender
                        </label>
                        <Field
                          as="select"
                          name="gender"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </Field>
                        <ErrorMessage
                          name="gender"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          PAN Card Number
                        </label>
                        <Field
                          type="text"
                          name="panCardNumber"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="panCardNumber"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Aadhaar Number
                        </label>
                        <Field
                          type="text"
                          name="aadhaarNumber"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="aadhaarNumber"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-4">
                      Address
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Street Address
                        </label>
                        <Field
                          type="text"
                          name="address.address_line"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="address.address_line"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          City
                        </label>
                        <Field
                          type="text"
                          name="address.city"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="address.city"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          State
                        </label>
                        <Field
                          type="text"
                          name="address.state"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="address.state"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Country
                        </label>
                        <Field
                          type="text"
                          name="address.country"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="address.country"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Pincode
                        </label>
                        <Field
                          type="text"
                          name="address.pincode"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="address.pincode"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-4">
                      Bank Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Account Holder Name
                        </label>
                        <Field
                          type="text"
                          name="bankDetails.accountHolderName"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="bankDetails.accountHolderName"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Bank Name
                        </label>
                        <Field
                          type="text"
                          name="bankDetails.bankName"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="bankDetails.bankName"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Account Number
                        </label>
                        <Field
                          type="text"
                          name="bankDetails.accountNumber"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="bankDetails.accountNumber"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          IFSC Code
                        </label>
                        <Field
                          type="text"
                          name="bankDetails.ifscCode"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="bankDetails.ifscCode"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Account Type
                        </label>
                        <Field
                          type="text"
                          name="bankDetails.accountType"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="bankDetails.accountType"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          UPI ID
                        </label>
                        <Field
                          type="text"
                          name="bankDetails.upiId"
                          className="mt-1 w-full p-2 border border-gray-300 rounded focus:ring-[#35A93E] focus:border-[#35A93E]"
                        />
                        <ErrorMessage
                          name="bankDetails.upiId"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                    </div>
                    {/* <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-4">
                      KYC Documents
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          PAN Card
                        </label>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => panCardRef.current.click()}
                            className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                          >
                            Choose File
                          </button>
                          <span className="text-sm text-gray-600">
                            {values.panCard ? values.panCard.name : "No file chosen"}
                          </span>
                          <input
                            type="file"
                            name="panCard"
                            ref={panCardRef}
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => setFieldValue("panCard", event.target.files[0])}
                          />
                        </div>
                        <ErrorMessage
                          name="panCard"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Aadhaar Card
                        </label>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => aadhaarCardRef.current.click()}
                            className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                          >
                            Choose File
                          </button>
                          <span className="text-sm text-gray-600">
                            {values.aadhaarCard ? values.aadhaarCard.name : "No file chosen"}
                          </span>
                          <input
                            type="file"
                            name="aadhaarCard"
                            ref={aadhaarCardRef}
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => setFieldValue("aadhaarCard", event.target.files[0])}
                          />
                        </div>
                        <ErrorMessage
                          name="aadhaarCard"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Passport or Voter ID
                        </label>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => passportOrVoterIdRef.current.click()}
                            className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                          >
                            Choose File
                          </button>
                          <span className="text-sm text-gray-600">
                            {values.passportOrVoterId ? values.passportOrVoterId.name : "No file chosen"}
                          </span>
                          <input
                            type="file"
                            name="passportOrVoterId"
                            ref={passportOrVoterIdRef}
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => setFieldValue("passportOrVoterId", event.target.files[0])}
                          />
                        </div>
                        <ErrorMessage
                          name="passportOrVoterId"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Photograph
                        </label>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => photographRef.current.click()}
                            className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                          >
                            Choose File
                          </button>
                          <span className="text-sm text-gray-600">
                            {values.photograph ? values.photograph.name : "No file chosen"}
                          </span>
                          <input
                            type="file"
                            name="photograph"
                            ref={photographRef}
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => setFieldValue("photograph", event.target.files[0])}
                          />
                        </div>
                        <ErrorMessage
                          name="photograph"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Cancelled Cheque
                        </label>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => cancelledChequeRef.current.click()}
                            className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                          >
                            Choose File
                          </button>
                          <span className="text-sm text-gray-600">
                            {values.cancelledCheque ? values.cancelledCheque.name : "No file chosen"}
                          </span>
                          <input
                            type="file"
                            name="cancelledCheque"
                            ref={cancelledChequeRef}
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => setFieldValue("cancelledCheque", event.target.files[0])}
                          />
                        </div>
                        <ErrorMessage
                          name="cancelledCheque"
                          component="div"
                          className="text-red-600 text-xs mt-1"
                        />
                      </div>
                    </div> */}
                    <div className="flex justify-center gap-4 mt-6">
                      <button
                        type="button"
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
                        onClick={handleEditClose}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-[#35A93E] text-white rounded hover:bg-[#2d8a33] transition"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Modal.Body>
        </Modal>

        {/* Document Edit Modal */}
        <EditDocumentModal
          show={showDocumentModal}
          onHide={handleDocumentModalClose}
          documentType={currentDocumentType}
          updateDocument={handleUpdateDocument}
        />

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
};

export default Profile;